import React from 'react';
import { Button, Container, Grid, Typography } from '@mui/material';
import { ContentFilter, Icon, SortingSelect } from 'components';
import { theme } from 'config';
import { inject, observer } from 'mobx-react';
import { Course, Curriculum, Package, Program } from 'models';
import { useTranslation } from 'react-i18next';
import { AuthStore, RoutingStore } from 'stores';
import { DataType, DataTypeContent, ICurriculum, UserType } from 'types';
import { AssignedFilterTypes, ShowContentTypes, SortTypes } from 'types/filter';
import { compare } from 'util/util';
import { Loading } from 'views';
import { ActivationFilter } from './activationFilter';
import { ContentCard } from './card';
import { CardGrid } from './cardGrid';
import { RoutingParams } from 'routes';
import { useParams } from 'react-router';


export interface ICardGridWrapper {
  authStore?:     AuthStore,
  routingStore?:  RoutingStore,
  contentType:    DataTypeContent | "all",
  title:          string,
  actionLabel?:   string,
  desc:           string,
  showSorting?:   boolean,
  showFiltering?: boolean
  showCompleted?: boolean,
  parent?:        Curriculum<ICurriculum>,
  useCatalog?:    boolean, // Decides whether content is loaded for that specific user or if the content catalog is used 
  defaultContentTypeFilter?: ShowContentTypes,
  onCardClick?(content: Curriculum<ICurriculum>): void,
  onActionClick?(): void,
}

export const CardGridWrapper = inject('authStore', 'routingStore')(observer((props: ICardGridWrapper): JSX.Element => {
  const { 
    authStore, 
    routingStore,
    contentType, 
    defaultContentTypeFilter, 
    title, 
    desc, 
    parent, 
    showSorting, 
    showFiltering,
    useCatalog, 
    showCompleted, 
    onCardClick, 
    onActionClick, 
    actionLabel 
  } = props;
  const params: RoutingParams = useParams<RoutingParams>();

  const { t } = useTranslation()

  const [courses,     setCourses]     = React.useState<Course[]>([]);
  const [packages,    setPackages]    = React.useState<Package[]>([]);
  const [programs,    setPrograms]    = React.useState<Program[]>([]);
  const [sort,        setSort]        = React.useState<SortTypes>(localStorage.getItem('bgi_sortTypes') as SortTypes || SortTypes.Alphabetical);
  const [filter,      setFilter]      = React.useState<AssignedFilterTypes>(localStorage.getItem('bgi_courseFilter') as AssignedFilterTypes || AssignedFilterTypes.All);
  const [showContent, setShowContent] = React.useState<ShowContentTypes>(
    (contentType === ShowContentTypes.All && !parent && localStorage.getItem('bgi_libraryFilter') as ShowContentTypes) || 
    defaultContentTypeFilter || 
    ShowContentTypes.All
  );

  React.useEffect(() => {
    setCourses([]);
    setPrograms([]);
    setPackages([]);

    const company_id: number = authStore.getCurUserType === UserType.Admin 
      ? Number(params.id)
      : undefined;

    if([DataType.Course, "all"].includes(contentType))
      Course.get(undefined, useCatalog, parent, company_id).then(async (c: Course[]) => {
        setCourses(c || []);
      })
    
    if([DataType.Program, "all"].includes(contentType) && (!parent || parent.data_type === DataType.Package))
      Program.get(undefined, useCatalog, parent, company_id).then((p: Program[]) => {
        setPrograms(p || []);
      });
    
    if([DataType.Package, "all"].includes(contentType) && !parent)
      Package.get(undefined, useCatalog, company_id).then((p: Package[]) => {
        setPackages(p || []);
      })

  }, [contentType, useCatalog, parent]);

const statistics = (useCatalog && authStore.getCurUser.statistics) ? 
    authStore.getCurUser.statistics[contentType as DataTypeContent]
    : undefined;

  const activeContent: Curriculum<ICurriculum>[] = React.useMemo(() => {
    let content: Curriculum<ICurriculum>[];

    if(parent) {
      content = parent.data_type === DataType.Package ? 
      [...programs]
      : [...courses];
    } else {
      switch(showContent) {
        case ShowContentTypes.All:
          content = [...courses, ...programs, ...packages];
          break;
        case ShowContentTypes.Courses:
          content = courses;
          break;
        case ShowContentTypes.Programs:
          content = programs;
          break;
        case ShowContentTypes.Packages:
          content = packages;
          break;
      }
    }
    const res = content.filter(c => c.progress !== 100).sort((a, b) => compare(a, b, sort));

    if(statistics && filter !== AssignedFilterTypes.All) {
      return res.filter((c) => statistics[c.id].num_users);
    }
    return res;

  }, [sort, showContent, filter, courses, programs, packages]);

  React.useEffect(() => {
    localStorage.setItem('bgi_libraryFilter', showContent);
    localStorage.setItem('bgi_courseFilter', filter);
    localStorage.setItem('bgi_sortTypes', sort);
  }, [showContent, filter, sort]);    

  const completedContent: Curriculum<ICurriculum>[] = React.useMemo(() => {
    return showCompleted ? [...courses, ...programs, ...packages].filter((c) => c.progress === 100) : [];
  }, [courses, programs, packages, showCompleted]);
  

  React.useEffect(() => {
    if(!useCatalog || !activeContent || !activeContent.length) return;

    const company_id: number = authStore.getCurUserType === UserType.Admin 
      ? Number(params.id)
      : undefined;

    authStore.getCurUser.loadStatistics({
      course_id:  courses.map(c => c.id),
      program_id: programs.map(c => c.id),
      package_id: packages.map(c => c.id),
      company_id
    });
  }, [activeContent, contentType, useCatalog]);

  const NoResultMessages = () => {
    if (activeContent.length > 0 && !showContent ) 
    return `${activeContent.length} ${t('results_showing')}`
    else if (activeContent.length === 0) 
    return t('no_results_found')
  };

  return (
    <Container sx={{padding:{xs: 4}, maxWidth: {lg: 'lg'}}}>
      <Grid pt={3} container>
        <Grid
          item
          xs             = {12}
          mb             = {3}
          display        = 'flex'
          justifyContent = 'space-between'
          >
          <Typography variant='h2'>{title}</Typography>
          {onActionClick && actionLabel &&
            <Button
            variant   = 'contained'
            startIcon = {<Icon iconId='add' />}
            onClick   = {onActionClick}
            >
              {actionLabel}
            </Button>
          }
        </Grid>
        <Grid xs={12} mb={3} item>
          <Typography variant='body1'>
            {desc} 
          </Typography>
        </Grid>
        <Grid
          item
          xs             = {12}
          display        = 'flex'
          justifyContent = 'space-between'
          alignItems     = 'center'
          mb             = {3}
          sx             = {{flexDirection: {xs: 'column', md: 'row'}, alignItems: {xs: 'flex-start'}}}
          >
          <Typography sx={{mb:{xs:2}}} variant='body1'>
            {NoResultMessages()}
          </Typography>
          <Grid display='flex' sx={{flexDirection: {xs: 'column', md: 'row'}, minWidth: {sm: '40rem'}}} gap={2}>
            {contentType === ShowContentTypes.All && !parent && 
              <ContentFilter onChange={setShowContent} value={showContent} />
            }
            {showFiltering &&
              <ActivationFilter value={filter} onChange={setFilter}/>
            }
            {showSorting && 
              <SortingSelect value={sort} onChange={setSort} />
            }
          </Grid>
        </Grid>
    </Grid>
    {routingStore.numLoading ? <Loading/> :
      <CardGrid>
        {activeContent.map((c) => (
          <ContentCard
            key             = {c.data_type + "_" + c.id}
            type            = {c.data_type}
            description     = {c.desc}
            userNumber      = {statistics ? statistics[c.id].num_users : undefined}
            finishedUsers   = {statistics ? statistics[c.id].num_finished : undefined}
            category        = {c.data_type === DataType.Course ? (c as Course).category : undefined}
            title           = {c.title}
            managerProgress = {statistics ? Math.round(statistics[c.id].progress) : undefined}
            userType        = {UserType.Participant}
            progress        = {useCatalog ? undefined : c.progress}
            onClick         = {() => onCardClick(c)}
          />
        ))}
      </CardGrid>
      }
      {showCompleted && completedContent.length > 0 && (
        <>
          <Grid mt={5} container>
            <Grid xs={12} mb={3} item>
              <Typography
                mb         = {3}
                variant    = 'h3'
                fontWeight = {theme.typography.fontWeightBold}
              >
                {t('completed_content')}
              </Typography>
              <Typography variant='body1'>
                {t('completed_content_description')}
              </Typography>
            </Grid>
          </Grid>
          <CardGrid>
            {completedContent.map((c) => (
              <ContentCard
                key           = {c.data_type + "_" + c.id}
                type          = {c.data_type}
                description   = {c.desc}
                userNumber    = {statistics ? statistics[c.id].num_users : undefined}
                category      = {c.data_type === DataType.Course ? (c as Course).category : undefined}
                title         = {c.title}
                userType      = {UserType.Participant}
                progress      = {Math.round(c.progress)}
                onClick         = {() => onCardClick(c)}
              />
            ))}
          </CardGrid>
        </>
      )}
    </Container>
  );
}));

